/**
 * Helpers for the RPP ad asset upload flow.
 *
 * The CMS never uploads files through the API server. The API returns a
 * presigned S3 POST target and the browser uploads the file directly to S3.
 * The S3 request must NOT carry the CMS JWT and must NOT set Content-Type
 * manually (the browser adds the multipart boundary).
 */

export enum RppAssetType {
    AD_VIDEO = 'ad_video',
    IMAGE = 'image',
}

export interface AssetConstraint {
    allowedContentTypes: string[];
    maxBytes: number;
}

const MEBIBYTE = 1024 * 1024;

const AD_VIDEO_CONSTRAINT: AssetConstraint = {
    allowedContentTypes: ['video/mp4', 'video/quicktime', 'video/webm'],
    maxBytes: 500 * MEBIBYTE,
};

const IMAGE_CONSTRAINT: AssetConstraint = {
    allowedContentTypes: [
        'image/jpeg',
        'image/png',
        'image/webp',
        'image/gif',
        'image/svg+xml',
    ],
    maxBytes: 20 * MEBIBYTE,
};

/** Constraints per asset type, matching the backend's allow-list. */
export function getAssetConstraint(assetType: RppAssetType): AssetConstraint {
    return assetType === RppAssetType.AD_VIDEO
        ? AD_VIDEO_CONSTRAINT
        : IMAGE_CONSTRAINT;
}

/** Value for an `<input type="file" accept="...">` attribute. */
export function acceptAttrFor(assetType: RppAssetType): string {
    return getAssetConstraint(assetType).allowedContentTypes.join(',');
}

function formatBytes(bytes: number): string {
    if (bytes >= MEBIBYTE) {
        return `${Math.round(bytes / MEBIBYTE)} MiB`;
    }
    if (bytes >= 1024) {
        return `${Math.round(bytes / 1024)} KiB`;
    }
    return `${bytes} B`;
}

/**
 * Validate a file client-side before requesting an upload target.
 * Returns an error message, or null when the file is acceptable.
 */
export function validateAssetFile(
    file: File,
    assetType: RppAssetType
): string | null {
    const { allowedContentTypes, maxBytes } = getAssetConstraint(assetType);

    if (file.size <= 0) {
        return 'The selected file is empty.';
    }
    if (!allowedContentTypes.includes(file.type)) {
        return `Unsupported file type "${
            file.type || 'unknown'
        }". Allowed: ${allowedContentTypes.join(', ')}.`;
    }
    if (file.size > maxBytes) {
        return `File is too large (${formatBytes(
            file.size
        )}). Maximum allowed is ${formatBytes(maxBytes)}.`;
    }
    return null;
}

/** Presigned POST target returned by `POST /rpp-assets/upload-target`. */
export interface UploadTarget {
    assetType: RppAssetType;
    key: string;
    uploadUrl: string;
    method: 'POST';
    fields: Record<string, string>;
    expiresInSeconds: number;
}

/** Error raised when the direct-to-S3 upload fails. */
export class RppUploadError extends Error {
    status?: number;

    constructor(message: string, status?: number) {
        super(message);
        this.name = 'RppUploadError';
        this.status = status;
    }
}

/**
 * Upload a file straight to S3 using a presigned POST target.
 *
 * Uses XMLHttpRequest so we get an upload progress signal while letting the
 * browser set the multipart Content-Type boundary. Deliberately attaches no
 * auth header — this request must never carry the CMS JWT.
 */
export function uploadFileToS3(
    target: Pick<UploadTarget, 'uploadUrl' | 'method' | 'fields'>,
    file: File,
    onProgress?: (percent: number) => void
): Promise<void> {
    const formData = new FormData();

    // Every returned field must be appended exactly as provided, before the file.
    Object.entries(target.fields).forEach(([name, value]) => {
        formData.append(name, value);
    });
    formData.append('file', file);

    return new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open(target.method, target.uploadUrl);

        if (onProgress) {
            xhr.upload.onprogress = (event) => {
                if (!event.lengthComputable) {
                    return;
                }
                const percent = Math.round((event.loaded / event.total) * 100);
                onProgress(Math.min(100, percent));
            };
        }

        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve();
            } else {
                reject(
                    new RppUploadError(
                        `Upload to storage failed (HTTP ${xhr.status}). The upload target may have expired — please try again.`,
                        xhr.status
                    )
                );
            }
        };
        xhr.onerror = () =>
            reject(new RppUploadError('Network error during upload.'));
        xhr.onabort = () => reject(new RppUploadError('Upload was aborted.'));

        xhr.send(formData);
    });
}
